import { ClientAddressModel } from "services/api/cplapi/CPLAPI";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { postcodeValidationRegex } from "cpl-react-common/components/Constants";
import { useSelector } from "react-redux";
import { selectGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";
import { selectHasDifferentBillingAddressSet } from "./ClientAddressesSlice";
import { toast } from "react-toastify";

export type ClientAddressDetailsProps = {
  ClientAddressDetails: ClientAddressModel;
  UpdateClientAddressDetailsCallback(clientEmailAddressDetails: ClientAddressModel): void;
  DeleteClientAddressDetailsCallback(cleintEmailAddressDetailsID: string): void;
};

const getValidationSchema = () =>
  Yup.object().shape({
    address: Yup.object().shape({
      fullAddress: Yup.string().required("Address is required"),
      postCode: Yup.string()
        .required("Postcode is required")
        .matches(postcodeValidationRegex, "Postcode not valid format"),
    }),
  });

const ClientAddressDetails: React.FC<ClientAddressDetailsProps> = ({
  ClientAddressDetails,
  UpdateClientAddressDetailsCallback,
  DeleteClientAddressDetailsCallback,
}) => {
  const hasBillingAddressSet = useSelector(selectHasDifferentBillingAddressSet(ClientAddressDetails.clientAddressID!));
  const { showEmailAddressEdit } = useSelector(selectGlobalSettings);
  const canEdit = showEmailAddressEdit !== false;

  function updateBillingAddress(newAddressDetails: ClientAddressModel) {
    if (hasBillingAddressSet) {
      toast.error(
        "Only one billing address can be set. Please remove the ‘Is Billing Address?’ selection from any other addresses before saving this"
      );
    } else {
      UpdateClientAddressDetailsCallback(newAddressDetails);
    }
  }

  function deleteBillingAddress() {
    if (ClientAddressDetails.billingAddress)
      toast.error(
        "This address is currently marked as the billing address. Please remove the ‘Is Billing Address?’ selection before deleting the address."
      );
    else {
      DeleteClientAddressDetailsCallback(ClientAddressDetails.clientAddressID!);
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={getValidationSchema()}
      initialValues={ClientAddressDetails}
      onSubmit={(newAddressDetails) => updateBillingAddress(newAddressDetails)}
    >
      {({ touched }) => (
        <Form>
          <div className="row m-1">
            <div className="col-12 col-lg-6">
              <label htmlFor={`${ClientAddressDetails.clientAddressID}FullAddress`} className="form-label required">
                Full Address
              </label>
              <Field
                data-test-id={`addresses-list__full-address-textarea-${ClientAddressDetails.clientAddressID}`}
                name="address.fullAddress"
                rows={4}
                className="form-control form-control-solid"
                component="textarea"
                disabled={!canEdit}
                id={`${ClientAddressDetails.clientAddressID}FullAddress`}
              />
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <ErrorMessage name="fullAddress" />
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-6 mt-3 mt-lg-0 ps-lg-4">
              <label htmlFor={`${ClientAddressDetails.clientAddressID}PostCode`} className="form-label">
                Postcode
              </label>
              <Field
                data-test-id={`addresses-list__postcode-input-${ClientAddressDetails.clientAddressID}`}
                name="address.postCode"
                className="form-control form-control-solid"
                disabled={!canEdit}
                id={`${ClientAddressDetails.clientAddressID}PostCode`}
              />
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <ErrorMessage name="address.postCode" />
                </div>
              </div>
              <label htmlFor={`${ClientAddressDetails.clientAddressID}Description`} className="form-label mt-3">
                Description
              </label>
              <Field
                data-test-id={`addresses-list__description-input-${ClientAddressDetails.clientAddressID}`}
                name="description"
                className="form-control form-control-solid"
                disabled={!canEdit}
                id={`${ClientAddressDetails.clientAddressID}Description`}
              />
            </div>
          </div>
          <div className="row m-1">
            <div className="col-sm-12 col-md-12 col-lg-3 align-self-center">
              <Field
                data-test-id={`addresses-list__billing-address-checkbox-${ClientAddressDetails.clientAddressID}`}
                name="billingAddress"
                type="checkbox"
                className="form-check-input"
                id={`${ClientAddressDetails.clientAddressID}BillingAddress`}
              />
              <label
                htmlFor={`${ClientAddressDetails.clientAddressID}BillingAddress`}
                className="form-check-label ms-2"
              >
                Is billing address?
              </label>
            </div>
            <div className="col-sm-12 col-md-12 col-lg-3  align-self-center">
              <Field
                data-test-id={`addresses-list__internal-billing-address-checkbox-${ClientAddressDetails.clientAddressID}`}
                name="internalBilling"
                type="checkbox"
                className="form-check-input"
                id={`${ClientAddressDetails.clientAddressID}InternalBillingAddress`}
              />
              <label
                htmlFor={`${ClientAddressDetails.clientAddressID}InternalBillingAddress`}
                className="form-check-label ms-2"
              >
                Is internal billing address?
              </label>
            </div>
            <div className="col-sm-12 col-md-12 col-lg-3  align-self-center">
              <Field
                data-test-id={`addresses-list__forwarding-address-checkbox-${ClientAddressDetails.clientAddressID}`}
                name="forwardingAddress"
                type="checkbox"
                className="form-check-input"
                id={`${ClientAddressDetails.clientAddressID}ForwardingAddress`}
              />
              <label
                htmlFor={`${ClientAddressDetails.clientAddressID}ForwardingAddress`}
                className="form-check-label ms-2"
              >
                Is forwarding address?
              </label>
            </div>
            <div className="col-sm-12 col-md-12 col-lg-3  text-end">
              <button
                data-test-id={`addresses-list__save-button-${ClientAddressDetails.clientAddressID}`}
                className="cpl-btn cpl-btn-success me-1"
                type="submit"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                disabled={
                  !(touched.address !== undefined
                    ? touched.address["fullAddress"] && touched.address["postcode"]
                    : false) &&
                  !touched.description &&
                  !touched.billingAddress &&
                  !touched.internalBilling &&
                  !touched.forwardingAddress
                }
              >
                Save
              </button>
              {canEdit && (
                <button
                  data-test-id={`addresses-list__delete-button-${ClientAddressDetails.clientAddressID}`}
                  className="cpl-btn cpl-btn-danger"
                  type="button"
                  onClick={() => deleteBillingAddress()}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
          <div className="row"></div>
        </Form>
      )}
    </Formik>
  );
};

export default ClientAddressDetails;
